import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, ShieldCheck, ShoppingBag, Truck } from "lucide-react";

/**
 * StoreBuyNowModal — one-tap Razorpay checkout for Store merch.
 * Loads /razorpay/checkout.js dynamically, calls /api/store/checkout to create
 * an order and /api/store/verify to confirm on success.
 */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function StoreBuyNowModal({ product, onClose, onSuccess }) {
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "" });
  const [busy, setBusy] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, discount }
  const [couponBusy, setCouponBusy] = useState(false);
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const subtotal = (product?.price || 0) * qty;
  const discount = coupon?.discount || 0;
  const shipping = (subtotal - discount) >= 999 ? 0 : 79;
  const total = Math.max(1, subtotal - discount) + shipping;

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/store/coupons/validate", { code: couponCode.trim().toUpperCase(), subtotal });
      if (data.valid) {
        setCoupon({ code: data.code, discount: data.discount });
        toast.success(`${data.code} applied — ₹${data.discount} off`);
      } else {
        setCoupon(null);
        toast.error(data.message || "Coupon invalid");
      }
    } catch { toast.error("Coupon check failed"); }
    finally { setCouponBusy(false); }
  };

  const pay = async (e) => {
    e?.preventDefault();
    if (!buyer.name || !buyer.email || !buyer.phone || !buyer.address_line1 || !buyer.city || !buyer.state || !buyer.pincode) {
      toast.error("Fill all delivery details");
      return;
    }
    setBusy(true);
    try {
      const rzpOk = await loadRazorpay();
      if (!rzpOk) throw new Error("Razorpay checkout couldn't load");
      const { data } = await api.post("/store/checkout", { product_id: product.id, quantity: qty, size, ...buyer, coupon_code: coupon?.code || "" });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Eventom Store",
        description: data.product.title,
        image: data.product.image_url,
        order_id: data.razorpay_order_id,
        prefill: { name: buyer.name, email: buyer.email, contact: buyer.phone },
        theme: { color: "#CCFF00" },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (rzpResp) => {
          try {
            await api.post("/store/verify", {
              razorpay_order_id: rzpResp.razorpay_order_id,
              razorpay_payment_id: rzpResp.razorpay_payment_id,
              razorpay_signature: rzpResp.razorpay_signature,
            });
            toast.success("Order placed — we'll ship it in 3–5 days.");
            onSuccess?.();
            onClose?.();
          } catch {
            toast.error("Payment received, verification failed. Team will reach out on WhatsApp.");
          } finally { setBusy(false); }
        },
      });
      rzp.open();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Checkout failed";
      toast.error(typeof msg === "string" ? msg : "Checkout failed");
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center overflow-y-auto" onClick={onClose} data-testid="store-buy-modal">
      <div className="relative w-full max-w-lg m-0 sm:m-4 rounded-t-3xl sm:rounded-3xl glass p-6 pb-8 max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 icon-circle !w-9 !h-9" data-testid="store-buy-close"><X className="w-4 h-4" /></button>
        <div className="flex items-start gap-3 mb-4">
          <img src={product.image_url} alt={product.title} className="w-16 h-16 rounded-2xl object-cover" />
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Eventom Store · Checkout</div>
            <div className="font-heading font-bold text-white text-sm mt-0.5 line-clamp-2">{product.title}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-0.5">₹{product.price.toLocaleString("en-IN")} · Eventom Original</div>
          </div>
        </div>

        <form onSubmit={pay} className="space-y-3">
          {(product.subcategory === "apparel" || !product.subcategory) && (
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Size</label>
              <div className="mt-1 flex gap-1.5">
                {["XS","S","M","L","XL","XXL"].map((s) => (
                  <button key={s} type="button" onClick={() => setSize(s)} data-testid={`store-size-${s}`}
                    className={`w-11 h-11 rounded-2xl border font-heading font-bold text-xs transition-all ${size === s ? "border-lime bg-lime/15 text-lime" : "border-white/15 text-white/70 hover:text-white"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Quantity</label>
            <div className="mt-1 inline-flex items-center gap-1 border border-white/15 rounded-full p-1">
              <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 rounded-full bg-white/10 text-white font-bold">–</button>
              <span className="w-10 text-center font-heading font-bold" data-testid="store-qty">{qty}</span>
              <button type="button" onClick={() => setQty(Math.min(10, qty + 1))} className="w-8 h-8 rounded-full bg-white/10 text-white font-bold">+</button>
            </div>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-widest text-lime pt-1">Delivery details</div>
          <div className="grid grid-cols-2 gap-2">
            <input required placeholder="Full name" value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} className="input-dark col-span-2" data-testid="store-buyer-name" />
            <input required type="email" placeholder="Email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} className="input-dark" data-testid="store-buyer-email" />
            <input required placeholder="Phone (WhatsApp)" value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} className="input-dark" data-testid="store-buyer-phone" />
            <input required placeholder="Address line 1" value={buyer.address_line1} onChange={(e) => setBuyer({ ...buyer, address_line1: e.target.value })} className="input-dark col-span-2" data-testid="store-buyer-addr1" />
            <input placeholder="Address line 2 (optional)" value={buyer.address_line2} onChange={(e) => setBuyer({ ...buyer, address_line2: e.target.value })} className="input-dark col-span-2" data-testid="store-buyer-addr2" />
            <input required placeholder="City" value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })} className="input-dark" data-testid="store-buyer-city" />
            <input required placeholder="State" value={buyer.state} onChange={(e) => setBuyer({ ...buyer, state: e.target.value })} className="input-dark" data-testid="store-buyer-state" />
            <input required placeholder="Pincode" value={buyer.pincode} onChange={(e) => setBuyer({ ...buyer, pincode: e.target.value })} className="input-dark col-span-2" data-testid="store-buyer-pincode" />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 space-y-2 text-xs">
            {/* Coupon */}
            <div className="flex items-center gap-1.5">
              <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Coupon (e.g. LAUNCH50)" className="input-dark flex-1 !py-2 !text-[11px]" data-testid="store-coupon-input" />
              {coupon ? (
                <button type="button" onClick={() => { setCoupon(null); setCouponCode(""); }} data-testid="store-coupon-remove" className="text-[10px] font-mono uppercase tracking-widest text-magenta px-3 py-2 rounded-full bg-magenta/10 border border-magenta/40">Remove</button>
              ) : (
                <button type="button" disabled={couponBusy} onClick={applyCoupon} data-testid="store-coupon-apply" className="text-[10px] font-mono uppercase tracking-widest text-lime px-3 py-2 rounded-full bg-lime/10 border border-lime/40">{couponBusy ? "…" : "Apply"}</button>
              )}
            </div>
            <div className="flex justify-between"><span className="text-muted">Subtotal</span><span className="text-white">₹{subtotal.toLocaleString("en-IN")}</span></div>
            {discount > 0 && <div className="flex justify-between"><span className="text-magenta">Coupon · {coupon.code}</span><span className="text-magenta">− ₹{discount.toLocaleString("en-IN")}</span></div>}
            <div className="flex justify-between"><span className="text-muted inline-flex items-center gap-1"><Truck className="w-3 h-3" /> Shipping {(subtotal - discount) >= 999 ? "(free above ₹999)" : ""}</span><span className="text-white">{shipping ? `₹${shipping}` : "Free"}</span></div>
            <div className="flex justify-between pt-1 border-t border-white/10"><span className="text-lime font-heading font-bold uppercase text-[11px] tracking-widest">Total</span><span className="text-lime font-fancy text-lg">₹{total.toLocaleString("en-IN")}</span></div>
          </div>

          <button type="submit" disabled={busy} className="w-full py-3.5 rounded-2xl bg-lime text-ink font-heading font-bold uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 hover:bg-lime/90 disabled:opacity-60" data-testid="store-buy-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} Pay ₹{total.toLocaleString("en-IN")} with Razorpay
          </button>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted text-center inline-flex items-center justify-center gap-1 w-full"><ShieldCheck className="w-3 h-3 text-lime" /> Secure payment · UPI / Cards / Netbanking</div>
        </form>
      </div>
    </div>,
    document.body
  );
}
